import { FC, RefObject, useContext } from 'react';
import { LoadingAndErrorHandler, PageErrorWithReload, Screen } from '../../components';
import {
    CurrentPlanProjectionDetailsForm,
    CurrentPlanProjectionDetailsFormContent,
} from '../../components/CurrentPlanProjectionDetails/CurrentPlanProjectionDetailsFormContent';
import { LoadingFields } from '../../components/LoadingFields/LoadingFields';
import { useCurrentPlanProjectionDetailsSubmitHandler, usePageSearchParams } from '../../hooks';
import { FormHandlingContext } from '../../context/FormHandlingContext';
import { CurrentPlanProjectionDetailsData, InputType, SkeletonElement } from '@xplan-pension-switching/common';
import { IressCol, IressRow, IressStack, FormRef } from '@iress-oss/ids-components';
import { Section } from '../../components/Section/Section';

const loadingPlaceholder =
    <IressStack gutter="lg">
        <IressRow gutter="lg">
            <IressCol span={{ md: 3 }}>
                <LoadingFields fields={[SkeletonElement.Heading]} testId='placeholder-plan-name' />
            </IressCol>
        </IressRow>
        <Section heading="Current values" headingLevel="h3">
            <IressRow gutter="lg">
                <IressCol span={{ md: 12 }}>
                    <LoadingFields fields={[InputType.TextArea]} testId='placeholder-current-values' />
                </IressCol>
            </IressRow>
        </Section>
        <Section heading="Ongoing contributions" headingLevel="h3">        
            <IressRow gutter="lg">
                <IressCol span={{ md: 12 }}>
                    <LoadingFields fields={[InputType.TextArea]} testId='placeholder-ongoing-contributions' />
                </IressCol>
            </IressRow>
        </Section>
        <Section heading="Projected values" headingLevel="h3">
            <IressRow gutter="lg">
                <IressCol span={{ md: 12 }}>
                    <LoadingFields fields={[InputType.TextArea]} testId='placeholder-projected-values' />
                </IressCol>
            </IressRow>
        </Section>
        <Section heading="Projected growth rates" headingLevel="h3">
            <IressRow gutter="lg">
                <IressCol span={{ md: 12 }}>
                    <LoadingFields fields={[InputType.TextArea]} testId='placeholder-projected-growth-rates' />
                </IressCol>
            </IressRow>
        </Section>
    </IressStack>;

export const CurrentPlanProjectionDetails: FC = () => {
    const { entityId, scenarioId, projectionId } = usePageSearchParams();
    const { formReference } = useContext(FormHandlingContext);
    const { submitHandler } = useCurrentPlanProjectionDetailsSubmitHandler(entityId, scenarioId!, projectionId!);

    return (
        <Screen title={'Current plan projection details'} withSubheading withButtons>
            <LoadingAndErrorHandler
                LoadingComponentFallback={loadingPlaceholder}
                ErrorComponentFallback={<PageErrorWithReload />}
            >
                <CurrentPlanProjectionDetailsForm
                    onSubmit={submitHandler}
                    ref={formReference as unknown as RefObject<FormRef<CurrentPlanProjectionDetailsData>>}
                    mode={'onBlur'}
                    data-testid={'current-plan-projection-details-form'}
                    shouldUnregister
                    updateErrorSummaryOnSubmit
                >
                    <CurrentPlanProjectionDetailsFormContent
                        entityId={entityId}
                        scenarioId={scenarioId!}
                        projectionId={projectionId!}
                    />
                </CurrentPlanProjectionDetailsForm>
            </LoadingAndErrorHandler>
        </Screen>
    );
};
